import { Card, CardContent, CardHeader, Input, ScrollArea, Switch } from '@ibsys/kit';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useDeleteKmGroup, useKmGroup, useUpdateKmGroup } from '@/entities/expert-report-km';
import { useHasPermission } from '@/entities/profile';
import { ConfirmationButton, SaveButton } from '@/shared/ui';

type KmGroupCardProps = {
  groupId: number;
  onDeleted: () => void;
  className?: string;
};

type KmGroupFormValues = {
  name: string;
  isHeader: boolean;
};

/**
 * Карточка группы структуры заключения на модели знаний. Группа с вложенными группами
 * оценку не несёт; структурная группа-заголовок отмечается отдельным признаком.
 */
export const KmGroupCard: FC<KmGroupCardProps> = ({ groupId, onDeleted, className }) => {
  const { t } = useTranslation(['common', 'expertReport']);
  const { data: group, isLoading } = useKmGroup(groupId);
  const { mutateAsync: updateGroup, isPending: isUpdating } = useUpdateKmGroup();
  const { mutateAsync: deleteGroup, isPending: isDeleting } = useDeleteKmGroup();
  const allowAdmin = useHasPermission('ADMIN_EXPERT_REPORT');

  const form = useForm<KmGroupFormValues>({ defaultValues: { name: '', isHeader: false } });
  const { control, reset, handleSubmit, formState } = form;

  useEffect(() => {
    if (group) reset({ name: group.name, isHeader: group.isHeader });
  }, [group, reset]);

  const onSubmit = async (values: KmGroupFormValues) => {
    await updateGroup({ groupId, body: { name: values.name.trim(), isHeader: values.isHeader } });
  };

  const handleDelete = async () => {
    await deleteGroup(groupId);
    onDeleted();
  };

  if (isLoading) {
    return (
      <Card className="w-full h-fit">
        <CardHeader>
          <div className="text-muted-foreground">{t('common:loading')}</div>
        </CardHeader>
      </Card>
    );
  }

  if (!group) {
    return (
      <Card className="w-full h-fit">
        <CardHeader>
          <div className="text-muted-foreground">{t('expertReport:selectItem')}</div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="space-y-4">
        <Input
          control={control}
          name="name"
          label={t('expertReport:km.groupName')}
          disabled={!allowAdmin}
          rules={{
            required: { value: true, message: t('common:required') },
            // Имя из одних пробелов required не отсекает, а сохранять группу с таким именем нельзя
            validate: (value) => (typeof value === 'string' && value.trim().length > 0) || t('common:required'),
          }}
        />

        <Switch control={control} name="isHeader" label={t('expertReport:isHeader')} disabled={!allowAdmin} />

        {allowAdmin && (
          <SaveButton
            className="justify-end"
            isDirty={formState.isDirty}
            isLoading={isUpdating}
            onClick={handleSubmit(onSubmit)}
            secondaryAction={
              <ConfirmationButton
                onConfirm={handleDelete}
                isLoadingConfirm={isDeleting}
                title={t('expertReport:km.removeGroup')}
                description={
                  <Trans
                    i18nKey="expertReport:km.removeGroupDescription"
                    values={{ groupName: group.name }}
                    components={{ strong: <strong /> }}
                  />
                }
                trigger={{
                  variant: 'outline',
                  color: 'destructive',
                  children: t('expertReport:km.deleteGroup'),
                }}
              />
            }
          />
        )}
      </CardHeader>

      <CardContent className="min-h-0 overflow-hidden">
        <ScrollArea className="h-full">
          <p className="text-sm text-muted-foreground">{t('expertReport:km.groupHint')}</p>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};
